/**
 * 世界层：地图文档的存取、场景注册与跨场景跳转。
 *
 * 地图文档（doc）是纯 JSON，能被地图编辑器增删改，也能导出成场景文件：
 *   { id, name, tileSize, rows:[字符画], chars:{}, spawn:{gx,gy}, props:[{type,gx,gy,state}] }
 * 全部存在 localStorage 里（编辑期够用、不依赖服务端），正式场景再导出到 src/scenes/ 下写成 .js。
 */
import { createDocScene } from '../scenes/custom.js';
import { SCENE_DOCS } from '../scenes/index.js';
import { Tags } from '../systems/tags.js';
import { CERT_DEFS, tagName } from './tags.js';
import { Repo } from './schema.js';

export const MAP_STORAGE_KEY = 'dongtianju:maps';

/* ------------------------------ 文档存取 ------------------------------ */

export function listMapDocs() {
  try {
    const raw = localStorage.getItem(MAP_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw).scenes || {};
  } catch { return {}; }
}

export function getMapDoc(id) {
  return listMapDocs()[id] || null;
}

export function saveMapDoc(doc) {
  const all = listMapDocs();
  all[doc.id] = { ...doc, updatedAt: Date.now() };
  try {
    localStorage.setItem(MAP_STORAGE_KEY, JSON.stringify({ version: 1, scenes: all }));
    return true;
  } catch { return false; }
}

export function deleteMapDoc(id) {
  const all = listMapDocs();
  delete all[id];
  try {
    localStorage.setItem(MAP_STORAGE_KEY, JSON.stringify({ version: 1, scenes: all }));
    return true;
  } catch { return false; }
}

/** 整份地图库导出 / 导入（merge=true 表示并入现有地图库） */
export function exportMapLib() {
  return { version: 1, scenes: listMapDocs() };
}

export function importMapLib(lib, { merge = true } = {}) {
  const incoming = (lib && lib.scenes) || {};
  const cur = merge ? listMapDocs() : {};
  const next = { ...cur };
  for (const [k, v] of Object.entries(incoming)) next[k] = v;
  try {
    localStorage.setItem(MAP_STORAGE_KEY, JSON.stringify({ version: 1, scenes: next }));
    return Object.keys(incoming).length;
  } catch { return 0; }
}

/* ------------------------------ 空白地图 ------------------------------ */

/** 造一张空地图：四周围墙，中间填满指定字符 */
export function emptyRows(w, h, fill = ',', border = '#') {
  const rows = [];
  for (let y = 0; y < h; y++) {
    let s = '';
    for (let x = 0; x < w; x++) {
      const edge = x === 0 || y === 0 || x === w - 1 || y === h - 1;
      s += edge ? border : fill;
    }
    rows.push(s);
  }
  return rows;
}

export function newMapDoc({ id, name, w = 40, h = 24, fill = ',', tileSize = 40 }) {
  return {
    id,
    name: name || id,
    tileSize,
    rows: emptyRows(w, h, fill),
    chars: {},
    spawn: { gx: Math.floor(w / 2), gy: Math.floor(h / 2) },
    props: [],
  };
}

/* ------------------------------ 场景注册 ------------------------------ */

/**
 * 把地图文档注册成可进入的场景：内置场景册（SCENE_DOCS）+ 地图编辑器改过的那份。
 * 编辑器改过的同名文档排在后面注册，等于覆盖内置版——试玩过的地方立刻生效。
 * 这样一来 `registerMapScenes(engine)` 就能把城郊、道路、城市与店铺全部挂进引擎，
 * 门户器物（state.to）才跳得过去。
 */
export function registerMapScenes(engine, docs = null) {
  const custom = listMapDocs();
  const edited = new Set(Object.keys(custom).filter((id) => id && id !== '__builder'));
  const list = [];
  const push = (doc) => {
    if (!doc || !doc.id || !doc.rows || !doc.rows.length) return;
    list.push(doc);
  };
  for (const doc of (Array.isArray(docs) ? docs : SCENE_DOCS)) {
    if (edited.has(doc.id)) continue;
    push(doc);
  }
  for (const [id, doc] of Object.entries(custom)) {
    if (id === '__builder') continue;
    push(doc);
  }
  const out = [];
  for (const doc of list) {
    engine.registerScene(createDocScene(engine, doc));
    out.push(doc.id);
  }
  return out;
}

/* ------------------------------ 场景跳转 ------------------------------ */

/**
 * 进入某个场景，可选指定落点（格坐标）。
 * 旧的场景实体在 enterScene 里被清空，所以这里统一把目标场景标成未加载，让它重跑一次 build，
 * 否则回来时会发现自己没有角色。
 */
export function gotoScene(engine, id, spawn) {
  const sc = engine.scenes.get(id);
  if (!sc) return false;
  const patch = { scene: id };
  if (spawn) {
    const ts = sc.tilemap ? sc.tilemap.tileSize : 40;
    patch.x = spawn.gx * ts + ts / 2;
    patch.y = spawn.gy * ts + ts / 2;
  } else {
    const tm = sc.tilemap;
    if (tm) { patch.x = tm.spawn.x; patch.y = tm.spawn.y; }
  }
  if (engine.db) Repo.savePlayer(engine.db, patch);
  sc.loaded = false;
  engine.enterScene(id);
  // build 里重新 use 了各系统，全局键要跟着挂回去
  if (engine.globalKeys && engine.scene && !engine.scene.systems.includes(engine.globalKeys)) {
    engine.scene.use(engine.globalKeys);
  }
  return true;
}

/**
 * 落点：走到对面，就该站在对面那扇「回这儿」的门口。
 * 场景文件里填的 state.spawn 只作兜底——门挪过一回，填的坐标就跟门错开一格，
 * 人从铺子里出来却站在隔壁，看着就是门户跟地图对不上。如今统一以对面那扇门为准。
 */
export function resolveSpawn(to, from, fallback = null) {
  if (from) {
    const back = (PORTALS.get(to) || []).find((p) => p.to === from);
    if (back) return { gx: back.gx, gy: back.gy };
  }
  return Array.isArray(fallback) && fallback.length === 2
    ? { gx: +fallback[0], gy: +fallback[1] }
    : null;
}

/** 从门户器物出发：读 state.to，落点交给 resolveSpawn */
export function gotoPortal(engine, prop) {
  const st = prop && prop.state ? prop.state : {};
  if (!st.to) return false;
  const from = (engine.scene && engine.scene.id) || (engine.db ? Repo.getPlayer(engine.db).scene : null);
  return gotoScene(engine, st.to, resolveSpawn(st.to, from, st.spawn));
}

/* ==========================================================================
 * 世界层：静态场景的注册、解锁、跳转
 *
 * 这一层只做三件事：
 * （依赖统一提到文件头，这里只留注释）
 *   1. 每个场景文件只报自己的地图与门户器物
 *   2. SCENES / PORTALS 两张索引从这里自动生成，不用手工维护
 *   3. 解锁、证件、过路费、门禁时段统一在这里判
 * ========================================================================== */

/** 区域定义：城郊 -> 城府 дорога -> 城市 */
export const REGIONS = {
  home: { id: 'home', name: '洞天居', tier: 0, order: 0 },
  suburb: { id: 'suburb', name: '城郊', tier: 0, order: 1 },
  road: { id: 'road', name: '城外道路', tier: 1, order: 2 },
  city: { id: 'city', name: '城市', tier: 2, order: 3 },
};

/**
 * 舆图坐标：一处在天下什么方位。x 向东为正，y 向南为正（数字越小越靠北）。
 * 城郊在西南，往东北一路进城——舆图上点怎么摆、路怎么连，全看这张表，
 * 摆得跟门户对得上，人才看得出自己在哪儿、下一步往哪儿走。
 */
export const GEO = {
  home: [0, 1],
  carport: [3, 2],
  neighbor_yard: [7, 1],
  riverside: [1, -3],
  country_road: [7, -3],
  highway: [11, -7],
  gate: [15, -11],

  main_street: [15, -16],
  general: [11, -13],
  library: [11, -20],
  onsen: [13, -21],

  yamen_sq: [10, -18],
  pharmacy: [5, -17],
  yamen_hall: [8, -21],

  market: [21, -16],
  pawn: [17, -19],
  garage: [25, -19],
  inn: [17, -13],
  eatery: [25, -13],

  riverfront: [27, -11],
  depot: [31, -13],

  fun_street: [21, -25],
  fun_room: [17, -28],
  junkyard: [25, -28],
  pet_shop: [26, -22],
};

/** 不在表上的地方（地图编辑器新造的）先按区域撒在那一带 */
const REGION_BASE = {
  home: [0, 1], suburb: [6, -2], road: [11, -8], city: [18, -18],
};

function hashStr(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

/** 取某处的坐标；没登记过的按名字散列撒开，免得全叠在一个点上 */
export function geoOf(id) {
  const g = GEO[id];
  if (g) return { x: g[0], y: g[1] };
  const info = SCENES.get(id) || {};
  const base = REGION_BASE[info.region] || REGION_BASE.city;
  const h = hashStr(String(id || ''));
  return {
    x: base[0] + ((h % 7) - 3) * 1.7,
    y: base[1] + ((Math.floor(h / 7) % 7) - 3) * 1.7,
    rough: true,
  };
}

/** id -> 场景档案（含 node/归类/ ShopId/class 等âttributes） */
export const SCENES = new Map();
/** from -> [{to, need, cost, hours, gx, gy}] 邻接表，自动从门户器物抽出 */
export const PORTALS = new Map();

/** 永远开放的落脚点（家门口一带不用解锁） */
export const ALWAYS_OPEN = new Set(['home', 'carport', 'country_road', 'neighbor_yard', 'riverside']);

(function buildWorldMaps() {
  SCENES.set('home', {
    id: 'home', name: '洞天居', region: 'home', tier: 0,
    indoor: true, kind: null, node: 'home', shopId: null, desc: '你自己的屋子。',
    geo: { x: GEO.home[0], y: GEO.home[1] },
  });
  for (const doc of SCENE_DOCS) {
    const m = doc.meta || {};
    SCENES.set(doc.id, {
      id: doc.id,
      name: doc.name || doc.id,
      region: m.region || 'city',
      geo: geoOf(doc.id),
      tier: m.tier == null ? 2 : m.tier,
      need: m.need || null,
      cost: m.cost || null,
      hours: m.hours || null,
      indoor: !!m.indoor,
      kind: m.kind || null,
      node: m.node || doc.id,
      shopId: m.shopId || null,
      desc: m.desc || '',
    });
  }
  for (const doc of SCENE_DOCS) {
    const list = (doc.props || [])
      .filter((p) => p.type === 'portal')
      .map((p) => {
        const st = p.state || {};
        return { to: st.to, need: st.need || null, cost: st.cost || null, hours: st.hours || null, label: st.label || '', gx: p.gx, gy: p.gy, from: doc.id };
      });
    PORTALS.set(doc.id, list);
  }
  // 自家院门写在 home.js 的 DEFAULT_PROPS 里（屋里那张地图不是文档场景，扫不到），
  // 这里补进邻接表，站在屋里问路才问得出「怎么出去」。改了院门记得同步这一行。
  PORTALS.set('home', [
    { to: 'carport', need: null, cost: null, hours: null, label: '车棚', gx: 14, gy: 18, from: 'home' },
  ]);
})();

/** 静态场景注册进引擎（探头/联锁/货币符都在 registerMapScenes 里处理） */
export function registerWorldScenes(engine) {
  return registerMapScenes(engine, SCENE_DOCS);
}

/** 场景信息，没有就给个兜底 */
export function sceneInfo(id) {
  return SCENES.get(id)
    || { id, name: id, region: 'city', tier: 2, node: id, kind: null, shopId: null, desc: '', geo: geoOf(id) };
}

export function sceneMeta(id) { return sceneInfo(id); }

export function sceneName(id) { return sceneInfo(id).name; }

export function alwaysOpen(id) { return ALWAYS_OPEN.has(id); }

/** 是否解锁（没有CONFIGURED>e.g. 也允许解锁新场景时此处只显示） */
export function unlocked(db, id) {
  if (ALWAYS_OPEN.has(id)) return true;
  if (!SCENES.has(id)) return false;
  return !!Repo.getFlag(db, `scene:${id}`);
}

export function unlockScene(db, id) {
  if (!SCENES.has(id)) return false;
  Repo.setFlag(db, `scene:${id}`, true);
  return true;
}

/** 舆图上画出来的：解锁过的 + 跟解锁过的直接相邻（知道有这个地方，不代表进得去） */
export function visible(db) {
  const known = new Set();
  for (const id of SCENES.keys()) {
    if (unlocked(db, id)) known.add(id);
  }
  for (const id of [...known]) {
    for (const p of PORTALS.get(id) || []) known.add(p.to);
  }
  return known;
}

/** 门禁检查：返回还没满足的条件列表，空数组代表放行 */
export function missingTags(db, need) {
  const out = [];
  if (!need) return out;
  if (need.certs) {
    for (const c of need.certs) if (!Tags.has(db, c)) out.push(`需${(CERT_DEFS[c] || {}).name || c}`);
  }
  if (need.tags) {
    for (const [t, v] of Object.entries(need.tags)) {
      const cur = Tags.get(db, t).total;
      if (cur < v) out.push(`${tagName(t)}${cur}/${v}`);
    }
  }
  return out;
}

/** 时段检查：hours=[起始分钟, 结束分钟]，null 表示全天 */
export function hourOpen(hours, world) {
  if (!hours || !Array.isArray(hours) || hours.length !== 2) return true;
  const m = (world && world.minute) || 0;
  const [a, b] = hours;
  return a <= b ? (m >= a && m <= b) : (m >= a || m <= b);
}

/** 广度优先找一条路的序列（含起点终点），找不到返回 null */
export function findRoute(from, to) {
  if (from === to) return [from];
  const q = [from];
  const prev = new Map([[from, null]]);
  while (q.length) {
    const cur = q.shift();
    for (const p of PORTALS.get(cur) || []) {
      if (prev.has(p.to)) continue;
      prev.set(p.to, cur);
      if (p.to === to) {
        const path = [to];
        let c = cur;
        while (c != null) { path.unshift(c); c = prev.get(c); }
        return path;
      }
      q.push(p.to);
    }
  }
  return null;
}

/**
 * 统一入口：检查证件 / 舆情 / 货币 / 门禁，然后才 gotoScene。
 * @param {object} engine
 * @param {string} id     目标场景
 * @param {object} opts   { portal, free, silent }
 * @returns {{ok:boolean, reason?:string}}
 */
export function travelTo(engine, id, opts = {}) {
  const db = engine.db;
  const info = sceneInfo(id);
  if (!SCENES.has(id)) return { ok: false, reason: `没有${info.name}这个地方` };

  const portal = opts.portal || null;
  const need = (portal && portal.need) || (portal === null ? null : null) || info.need || null;
  if (!opts.free) {
    const miss = missingTags(db, need);
    if (miss.length) return { ok: false, reason: `过不去：${miss.join('、')}` };
    const cost = portal && portal.cost != null ? portal.cost : info.cost;
    if (cost) {
      const p = Repo.getPlayer(db);
      if ((p.cash || 0) < cost) return { ok: false, reason: `钱不够，要${cost}钱` };
      Repo.savePlayer(db, { cash: (p.cash || 0) - cost });
    }
  }
  if (!opts.keepLocked && !unlocked(db, id)) unlockScene(db, id);

  const from = (engine.scene && engine.scene.id) || null;
  const spawn = opts.spawn || resolveSpawn(id, from, portal && portal.spawn);
  const done = gotoScene(engine, id, spawn);
  if (!done && !opts.silent) return { ok: false, reason: `${info.name}还没修好` };
  return { ok: true };
}

